import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { AdjustStockPayload, IssueStockPayload, StockItem, StockMovement, InventoryDashboard } from "../../services/stockItemService";
import {
    addNewStockItem,
    adjustStock as adjustStockApi,
    deleteStockItem as deleteStockItemApi,
    getStockItems,
    getStockMovements,
    getInventoryDashboard,
    getLowStockItems,
    issueStock as issueStockApi,
    StockItemCreatePayload,
    StockItemUpdatePayload,
    updateStockItem as updateStockItemApi
} from "../../services/stockItemService";

export const initialState = {
    stockItems: [],
    loading: false,
    error: null,
    totalCount: 0,
    totalPages: 0,
    currentPage: 1,
    selectedStockItem: null,
    stockMovements: [] as StockMovement[],
    movementsLoading: false,
    movementsError: null as any,
    dashboard: null as InventoryDashboard | null,
    dashboardLoading: false,
    dashboardError: null as any,
    lowStockItems: [] as StockItem[],
    lowStockLoading: false,
    lowStockError: null as any,
};

export const fetchStockItems = createAsyncThunk(
    "stockItems/fetchStockItems",
    async ({ pageNumber, pageSize }: { pageNumber: number; pageSize: number }) => {
        const response = await getStockItems(pageNumber, pageSize);
        return response;
    }
);

export const addStockItem = createAsyncThunk(
    "stockItems/addStockItem",
    async (stockItem: StockItemCreatePayload, { rejectWithValue }) => {
        try {
            const response = await addNewStockItem(stockItem);
            return response;
        } catch (error: any) {
            // Extract error message from API response
            // Error could be:
            // 1. Error object with message property (from throw new Error(response.message))
            // 2. String error message
            // 3. Axios error with response.data.message
            // 4. Validation errors in response.data.errors (field-level errors)
            let errorMessage = "Failed to create stock item";
            
            if (typeof error === 'string') {
                errorMessage = error;
            } else if (error?.message) {
                errorMessage = error.message;
            } else if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error?.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error?.response?.data?.title) {
                errorMessage = error.response.data.title;
            } else if (error?.response?.data?.errors) {
                // Handle validation errors - combine all field errors into a single message
                const validationErrors = error.response.data.errors;
                const errorMessages = Object.entries(validationErrors)
                    .map(([field, messages]) => {
                        const fieldMessages = Array.isArray(messages) ? messages : [messages];
                        return `${field}: ${fieldMessages.join(', ')}`;
                    })
                    .join('; ');
                errorMessage = errorMessages || "Validation failed";
            }
            
            return rejectWithValue(errorMessage);
        }
    }
);

export const updateStockItem = createAsyncThunk(
    "stockItems/updateStockItem",
    async ({ id, data }: { id: string; data: StockItemUpdatePayload }, { rejectWithValue }) => {
        try {
            const response = await updateStockItemApi(id, data);
            return response;
        } catch (error: any) {
            // Extract error message from API response
            // Error could be:
            // 1. Error object with message property (from throw new Error(response.message))
            // 2. String error message
            // 3. Axios error with response.data.message
            // 4. Validation errors in response.data.errors (field-level errors)
            let errorMessage = "Failed to update stock item";
            
            if (typeof error === 'string') {
                errorMessage = error;
            } else if (error?.message) {
                errorMessage = error.message;
            } else if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error?.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error?.response?.data?.title) {
                errorMessage = error.response.data.title;
            } else if (error?.response?.data?.errors) {
                // Handle validation errors - combine all field errors into a single message
                const validationErrors = error.response.data.errors;
                const errorMessages = Object.entries(validationErrors)
                    .map(([field, messages]) => {
                        const fieldMessages = Array.isArray(messages) ? messages : [messages];
                        return `${field}: ${fieldMessages.join(', ')}`;
                    })
                    .join('; ');
                errorMessage = errorMessages || "Validation failed";
            }
            
            return rejectWithValue(errorMessage);
        }
    }
);

export const deleteStockItem = createAsyncThunk(
    "stockItems/deleteStockItem",
    async (id: string) => {
        await deleteStockItemApi(id);
        return id;
    }
);

export const issueStock = createAsyncThunk(
    "stockItems/issueStock",
    async (payload: IssueStockPayload) => {
        await issueStockApi(payload);
        // Return payload for refreshing the list
        return payload;
    }
);

export const adjustStock = createAsyncThunk(
    "stockItems/adjustStock",
    async (payload: AdjustStockPayload, { rejectWithValue }) => {
        try {
            await adjustStockApi(payload);
            // Return payload for refreshing the list
            return payload;
        } catch (error: any) {
            // Extract error message from API response
            // Error could be:
            // 1. Error object with message property (from throw new Error(response.message))
            // 2. String error message
            // 3. Axios error with response.data.message
            let errorMessage = "Failed to adjust stock";
            
            if (typeof error === 'string') {
                errorMessage = error;
            } else if (error?.message) {
                errorMessage = error.message;
            } else if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error?.response?.data?.error) {
                errorMessage = error.response.data.error;
            }
            
            // Debug logging
            console.log("adjustStock thunk error:", error);
            console.log("Extracted error message:", errorMessage);
            
            return rejectWithValue(errorMessage);
        }
    }
);

export const fetchStockMovements = createAsyncThunk(
    "stockItems/fetchStockMovements",
    async ({ productId, startDate, endDate }: { productId: string; startDate?: string; endDate?: string }) => {
        const response = await getStockMovements(productId, startDate, endDate);
        return response;
    }
);

export const fetchInventoryDashboard = createAsyncThunk(
    "stockItems/fetchInventoryDashboard",
    async (locationId?: string) => {
        const response = await getInventoryDashboard(locationId);
        return response;
    }
);

export const fetchLowStockItems = createAsyncThunk(
    "stockItems/fetchLowStockItems",
    async (tenantId?: string | null) => {
        const response = await getLowStockItems(tenantId);
        return response;
    }
);

const stockItemSlice = createSlice({
    name: "stockItems",
    initialState,
    reducers: {
        selectStockItem(state: any, action: PayloadAction<string>) {
            state.selectedStockItem =
                state.stockItems.find((item: StockItem) => item.id === action.payload) || null;
        },
        clearError(state: any) {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Fetch
        builder.addCase(fetchStockItems.pending, (state: any) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchStockItems.fulfilled, (state: any, action: any) => {
            state.loading = false;
            // Handle pagination response structure
            let stockItems = [];
            if (action.payload && action.payload.items) {
                stockItems = action.payload.items;
                state.totalCount = action.payload.totalCount;
                state.totalPages = action.payload.totalPages;
                state.currentPage = action.payload.pageNumber;
            } else if (Array.isArray(action.payload)) {
                stockItems = action.payload;
            }

            state.stockItems = stockItems;
        });
        builder.addCase(fetchStockItems.rejected, (state: any, action: any) => {
            state.loading = false;
            // Store error as string - same pattern as Lead management
            // When not using rejectWithValue, error is in action.error.message
            const errorMessage = action.error?.message || action.payload || "Failed to fetch stock items";
            state.error = typeof errorMessage === 'string' ? errorMessage : String(errorMessage);
        });

        // Add
        builder.addCase(addStockItem.pending, (state: any) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(addStockItem.fulfilled, (state: any, action: any) => {
            state.loading = false;
            // Handle response - could be wrapped in data property, result property, or direct
            const response = action.payload?.data || action.payload;
            const stockItem = response?.result || response;
            if (stockItem && stockItem.id) {
                // Check if stock item already exists (avoid duplicates)
                const exists = state.stockItems.some((item: StockItem) => item.id === stockItem.id);
                if (!exists) {
                    state.stockItems.push(stockItem);
                    state.totalCount = (state.totalCount || 0) + 1;
                }
            }
        });
        builder.addCase(addStockItem.rejected, (state: any, action: any) => {
            state.loading = false;
            // Extract error message - when using rejectWithValue, error is in action.payload
            const errorMessage = action.payload || action.error?.message || "Failed to create stock item";
            state.error = typeof errorMessage === 'string' ? errorMessage : String(errorMessage);
        });

        // Update
        builder.addCase(updateStockItem.pending, (state: any) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(updateStockItem.fulfilled, (state: any, action: any) => {
            state.loading = false;
            // Handle response - could be wrapped in data property, result property, or direct
            const response = action.payload?.data || action.payload;
            const stockItem = response?.result || response;
            if (stockItem && stockItem.id) {
                const index = state.stockItems.findIndex((item: StockItem) => item.id === stockItem.id);
                if (index !== -1) {
                    state.stockItems[index] = stockItem;
                }
            }
        });
        builder.addCase(updateStockItem.rejected, (state: any, action: any) => {
            state.loading = false;
            // Extract error message - when using rejectWithValue, error is in action.payload
            const errorMessage = action.payload || action.error?.message || "Failed to update stock item";
            state.error = typeof errorMessage === 'string' ? errorMessage : String(errorMessage);
        });

        // Delete
        builder.addCase(deleteStockItem.pending, (state: any) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(deleteStockItem.fulfilled, (state: any, action: any) => {
            state.loading = false;
            state.stockItems = state.stockItems.filter((item: StockItem) => item.id !== action.payload);
        });
        builder.addCase(deleteStockItem.rejected, (state: any, action: any) => {
            state.loading = false;
            // Store error as string - same pattern as Lead management
            const errorMessage = action.error?.message || action.payload || "Failed to delete stock item";
            state.error = typeof errorMessage === 'string' ? errorMessage : String(errorMessage);
        });

        // Issue Stock
        builder.addCase(issueStock.pending, (state: any) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(issueStock.fulfilled, (state: any) => {
            state.loading = false;
            // Stock items will be refreshed from the list
        });
        builder.addCase(issueStock.rejected, (state: any, action: any) => {
            state.loading = false;
            // Store error as string - same pattern as Lead management
            const errorMessage = action.error?.message || action.payload || "Failed to issue stock";
            state.error = typeof errorMessage === 'string' ? errorMessage : String(errorMessage);
        });

        // Adjust Stock
        builder.addCase(adjustStock.pending, (state: any) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(adjustStock.fulfilled, (state: any) => {
            state.loading = false;
            // Stock items will be refreshed from the list
        });
        builder.addCase(adjustStock.rejected, (state: any, action: any) => {
            state.loading = false;
            // Store error as string - same pattern as Lead management
            const errorMessage = action.error?.message || action.payload || "Failed to adjust stock";
            state.error = typeof errorMessage === 'string' ? errorMessage : String(errorMessage);
        });

        // Fetch Stock Movements
        builder.addCase(fetchStockMovements.pending, (state: any) => {
            state.movementsLoading = true;
            state.movementsError = null;
        });
        builder.addCase(fetchStockMovements.fulfilled, (state: any, action: any) => {
            state.movementsLoading = false;
            state.stockMovements = Array.isArray(action.payload) ? action.payload : [];
        });
        builder.addCase(fetchStockMovements.rejected, (state: any, action: any) => {
            state.movementsLoading = false;
            state.movementsError = action.payload || "Failed to fetch stock movements";
        });

        // Fetch Inventory Dashboard
        builder.addCase(fetchInventoryDashboard.pending, (state: any) => {
            state.dashboardLoading = true;
            state.dashboardError = null;
        });
        builder.addCase(fetchInventoryDashboard.fulfilled, (state: any, action: any) => {
            state.dashboardLoading = false;
            state.dashboard = action.payload;
        });
        builder.addCase(fetchInventoryDashboard.rejected, (state: any, action: any) => {
            state.dashboardLoading = false;
            state.dashboardError = action.payload || "Failed to fetch inventory dashboard";
        });

        // Fetch Low Stock Items
        builder.addCase(fetchLowStockItems.pending, (state: any) => {
            state.lowStockLoading = true;
            state.lowStockError = null;
        });
        builder.addCase(fetchLowStockItems.fulfilled, (state: any, action: any) => {
            state.lowStockLoading = false;
            state.lowStockItems = Array.isArray(action.payload) ? action.payload : [];
        });
        builder.addCase(fetchLowStockItems.rejected, (state: any, action: any) => {
            state.lowStockLoading = false;
            state.lowStockError = action.payload || "Failed to fetch low stock items";
        });
    },
});

export const { selectStockItem, clearError } = stockItemSlice.actions;

export const selectStockItemList = (state: any) => state.StockItems.stockItems;
export const selectStockItemById = (state: any, id: string) =>
    state.StockItems.stockItems.find((item: StockItem) => item.id === id);
export const selectSelectedStockItem = (state: any) => state.StockItems.selectedStockItem;
export const selectStockItemLoading = (state: any) => state.StockItems.loading;
export const selectStockItemError = (state: any) => state.StockItems.error;
export const selectStockItemTotalCount = (state: any) => state.StockItems.totalCount;
export const selectStockMovements = (state: any) => state.StockItems.stockMovements;
export const selectStockMovementsLoading = (state: any) => state.StockItems.movementsLoading;
export const selectStockMovementsError = (state: any) => state.StockItems.movementsError;
export const selectInventoryDashboard = (state: any) => state.StockItems.dashboard;
export const selectInventoryDashboardLoading = (state: any) => state.StockItems.dashboardLoading;
export const selectInventoryDashboardError = (state: any) => state.StockItems.dashboardError;
export const selectLowStockItems = (state: any) => state.StockItems.lowStockItems;
export const selectLowStockItemsLoading = (state: any) => state.StockItems.lowStockLoading;
export const selectLowStockItemsError = (state: any) => state.StockItems.lowStockError;

export default stockItemSlice.reducer;
